import { useEffect, useReducer } from "react";
import type { ApprovalDTO, ConversationDTO, MessageDTO, PlatformEvent, RunDTO, SessionDTO } from "../../shared/types";
import { get } from "./api";
import { realtime } from "./ws";

export type RunView = Pick<RunDTO, "id" | "agentSlug" | "agentName" | "mode" | "depth" | "parentRunId" | "task" | "status"> &
  Partial<Pick<RunDTO, "result" | "error" | "costUsd" | "paidBy" | "inputTokens" | "outputTokens" | "startedAt" | "finishedAt" | "createdAt" | "model" | "defaultModel" | "subject">>;

export type ConversationState = {
  loading: boolean;
  error: string | null;
  conversation: ConversationDTO | null;
  messages: MessageDTO[];
  runs: Record<string, RunView>;
  runOrder: string[];
  events: Record<string, PlatformEvent[]>;
  streaming: Record<string, string>;
  /** The requests the messages carry, by id: what a card shows, kept up to date as it is answered. */
  approvals: Record<string, ApprovalDTO>;
  /** The conversation's sessions, oldest first: each one after the first draws a divider. */
  sessions: SessionDTO[];
};

type Action =
  | { type: "reset" }
  | { type: "loaded"; conversation: ConversationDTO; messages: MessageDTO[]; runs: RunDTO[]; approvals: ApprovalDTO[]; sessions: SessionDTO[]; events: PlatformEvent[] }
  | { type: "error"; error: string }
  | { type: "event"; event: PlatformEvent };

const initial: ConversationState = { loading: true, error: null, conversation: null, messages: [], runs: {}, runOrder: [], events: {}, streaming: {}, approvals: {}, sessions: [] };

function addEvent(state: ConversationState, e: PlatformEvent): ConversationState {
  if (e.seq <= 0) return state;
  const list = state.events[e.runId] ?? [];
  if (list.some((x) => x.seq === e.seq)) return state;
  const next = [...list, e].sort((a, b) => a.seq - b.seq);
  return { ...state, events: { ...state.events, [e.runId]: next } };
}

function upsertApproval(state: ConversationState, a: ApprovalDTO | undefined): ConversationState {
  if (!a?.id) return state;
  return { ...state, approvals: { ...state.approvals, [a.id]: a } };
}

function applyRunEvent(state: ConversationState, e: PlatformEvent): ConversationState {
  const p = e.payload ?? {};
  const existing = state.runs[e.runId];
  switch (e.type) {
    case "run.queued": {
      // Put back in the queue (a platform restart, or a Claude login we cannot use): it is waiting, not thinking.
      if (existing) return p.requeued ? { ...state, runs: { ...state.runs, [e.runId]: { ...existing, status: "queued", startedAt: undefined } } } : state;
      const run: RunView = {
        id: e.runId, agentSlug: p.agent, agentName: p.agentName ?? p.agent, mode: p.mode, depth: p.depth ?? 0,
        parentRunId: p.parentRunId ?? null, task: p.task ?? "", status: "queued", createdAt: e.ts,
      };
      return { ...state, runs: { ...state.runs, [e.runId]: run }, runOrder: [...state.runOrder, e.runId] };
    }
    case "run.started":
      return existing && existing.status === "queued" ? { ...state, runs: { ...state.runs, [e.runId]: { ...existing, status: "running", startedAt: e.ts } } } : state;
    case "run.finished":
    case "run.failed":
    case "run.cancelled":
      if (!existing) return state;
      return {
        ...state,
        runs: {
          ...state.runs,
          [e.runId]: {
            ...existing, status: p.status ?? e.type.slice(4), result: p.result, error: p.error, costUsd: p.costUsd, paidBy: p.paidBy,
            inputTokens: p.inputTokens, outputTokens: p.outputTokens, finishedAt: e.ts,
          },
        },
        streaming: { ...state.streaming, [e.runId]: "" },
      };
    default:
      return state;
  }
}

function reducer(state: ConversationState, action: Action): ConversationState {
  switch (action.type) {
    case "reset":
      return initial;
    case "error":
      return { ...state, loading: false, error: action.error };
    case "loaded": {
      const runs: Record<string, RunView> = {};
      for (const r of action.runs) runs[r.id] = r;
      const approvals: Record<string, ApprovalDTO> = {};
      for (const a of action.approvals ?? []) approvals[a.id] = a;
      let next: ConversationState = {
        ...initial, loading: false, conversation: action.conversation, messages: action.messages, runs, runOrder: action.runs.map((r) => r.id), approvals,
        sessions: action.sessions ?? [],
      };
      for (const e of action.events) next = addEvent(next, e);
      return next;
    }
    case "event": {
      const e = action.event;
      if (e.type === "assistant.delta") {
        if (e.payload?.parentToolUseId) return state;
        return { ...state, streaming: { ...state.streaming, [e.runId]: (state.streaming[e.runId] ?? "") + (e.payload?.text ?? "") } };
      }
      if (e.type === "approval.updated") return upsertApproval(state, e.payload as ApprovalDTO);
      let next = addEvent(state, e);
      if (e.type === "assistant.message") next = { ...next, streaming: { ...next.streaming, [e.runId]: "" } };
      if (e.type === "message.created") {
        const m = e.payload as MessageDTO & { approval?: ApprovalDTO };
        if (!next.messages.some((x) => x.id === m.id)) next = { ...next, messages: [...next.messages, m] };
        // A message that raced the answer of the turn it was added to arrives again, moved to a turn of its own.
        else next = { ...next, messages: next.messages.map((x) => (x.id === m.id && m.runId && x.runId !== m.runId ? { ...x, runId: m.runId } : x)) };
        next = upsertApproval(next, m.approval);
      }
      return applyRunEvent(next, e);
    }
  }
}

export function useConversation(conversationId: string | undefined) {
  const [state, dispatch] = useReducer(reducer, initial);

  useEffect(() => {
    dispatch({ type: "reset" });
    if (!conversationId) return;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      try {
        const [detail, events] = await Promise.all([
          get<{ conversation: ConversationDTO; messages: MessageDTO[]; runs: RunDTO[]; approvals: ApprovalDTO[]; sessions: SessionDTO[] }>(`/api/conversations/${conversationId}`),
          get<PlatformEvent[]>(`/api/conversations/${conversationId}/events`),
        ]);
        if (cancelled) return;
        dispatch({ type: "loaded", ...detail, events });
        const since: Record<string, number> = {};
        for (const e of events) since[e.runId] = Math.max(since[e.runId] ?? 0, e.seq);
        unsubscribe = realtime.subscribe(`conversation:${conversationId}`, (event) => dispatch({ type: "event", event }), since);
      } catch (err) {
        if (!cancelled) dispatch({ type: "error", error: (err as Error).message });
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [conversationId]);

  return state;
}
